/**
 * Document utility functions for the chat application
 */

import config from '../config';

/**
 * Format file size in human readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (!bytes) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Get appropriate icon for document type
 * @param {string} type - Document type (PDF, DOC, etc.)
 * @returns {string} Emoji icon for the document type
 */
export const getDocumentIcon = (type) => {
  const iconMap = {
    'PDF': '📄',
    'DOC': '📝',
    'DOCX': '📝',
    'XLS': '📊',
    'XLSX': '📊',
    'PPT': '📈',
    'PPTX': '📈',
    'ZIP': '🗜️',
    'RAR': '🗜️',
    '7Z': '🗜️',
    'TXT': '📃',
    'CSV': '📋',
    'RTF': '📝'
  };
  
  return iconMap[type] || '📄';
};

/**
 * Get file extension from filename
 * @param {string} filename - The filename
 * @returns {string} File extension without the dot
 */
export const getFileExtension = (filename) => {
  return filename.slice((filename.lastIndexOf(".") - 1 >>> 0) + 2);
};

/**
 * Check if file type is supported
 * @param {string} mimeType - MIME type of the file
 * @returns {boolean} True if file type is supported
 */
export const isSupportedDocumentType = (mimeType) => {
  const supportedTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'text/plain',
    'text/csv',
    'application/rtf',
    'application/zip',
    'application/x-rar-compressed',
    'application/x-7z-compressed'
  ];
  
  return supportedTypes.includes(mimeType) || mimeType.startsWith('text/');
};

/**
 * Get document type from MIME type
 * @param {string} mimeType - MIME type of the file
 * @param {string} filename - Filename for fallback
 * @returns {string} Document type abbreviation
 */
export const getDocumentType = (mimeType, filename) => {
  const typeMap = {
    'application/pdf': 'PDF',
    'application/msword': 'DOC',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
    'application/vnd.ms-excel': 'XLS',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
    'application/vnd.ms-powerpoint': 'PPT',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PPTX',
    'text/plain': 'TXT',
    'text/csv': 'CSV',
    'application/rtf': 'RTF',
    'application/zip': 'ZIP',
    'application/x-rar-compressed': 'RAR',
    'application/x-7z-compressed': '7Z'
  };

  if (typeMap[mimeType]) {
    return typeMap[mimeType];
  }

  // Fallback to file extension
  const extension = getFileExtension(filename).toUpperCase();
  return extension || 'FILE';
};

/**
 * Validate file size
 * @param {number} size - File size in bytes
 * @param {number} maxSize - Maximum allowed size in bytes
 * @returns {boolean} True if file size is valid
 */
export const isValidFileSize = (size, maxSize = 50 * 1024 * 1024) => {
  return size <= maxSize;
};

/**
 * Generate download URL for document
 * @param {string} documentUrl - Document URL path
 * @param {string} baseUrl - Base URL (should be passed from component using config.apiUrl)
 * @returns {string} Full download URL
 */
export const getDocumentDownloadUrl = (documentUrl, baseUrl = null) => {
  // Use config.apiUrl as default if baseUrl not provided
  if (!baseUrl) {
    baseUrl = config.apiUrl;
  }
  return `${baseUrl}${documentUrl}`;
};

/**
 * Check if a message is a document message
 * @param {Object} message - Message object
 * @returns {boolean} True if message is a document
 */
export const isDocumentMessage = (message) => {
  return message.type === 'document' || message.messageType === 'document';
};

/**
 * Get document preview text for notifications or summaries
 * @param {Object} message - Document message object
 * @returns {string} Preview text
 */
export const getDocumentPreview = (message) => {
  if (!isDocumentMessage(message)) return '';
  return `📄 ${message.documentName || 'Document'}`;
};

/**
 * Validate document message structure
 * @param {Object} message - Message object to validate
 * @returns {boolean} True if message has required document fields
 */
export const isValidDocumentMessage = (message) => {
  return isDocumentMessage(message) && 
         message.documentUrl && 
         message.documentName;
};
